import { scalingPx } from 'util/css';
import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, ReactNode, useMemo } from 'react';

import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { useProfile } from 'hooks';
import { Image, Link } from 'components/lib';
import { Routes } from 'services/routes';
import { RoleMap, useGetRole } from 'hooks/useGetRole';
import { useOrganizationsApi } from 'services/api/hooks/useOrganizationsApi.hook';
import useSWR from 'swr';
import { FULL_WIDTH } from '../../../theme';
import { CampusLushLogo } from '../CampusLushLogo';
import { ButtonLink } from '../Button/ButtonLink';
import { NotificationButton } from '../NotificationButton';
import { colorWhite } from 'customColors';
export interface HeaderProps {
  showActions?: boolean;
  loggedIn?: boolean;
  showLogo?: boolean;
  showSearch?: boolean;
  searchBar?: ReactNode;
  className?: string;
}

export const MENU_ICONS = {
  home: '/icons/menu/home-nav.svg',
  homeOutline: '/icons/menu/home-nav-outline.svg',
  explore: '/icons/menu/explore-nav.svg',
  exploreOutline: '/icons/menu/explore-nav-outline.svg',
  events: '/icons/menu/local_activity-nav.svg',
  eventsOutline: '/icons/menu/local_activity-nav-outline.svg',
  profile: '/icons/menu/profile-nav.svg',
  profileOutline: '/icons/menu/profile-nav-outline.svg',
  adminNav: '/icons/menu/admin-nav.svg',
  adminNavOutline: '/icons/menu/admin-nav-outline.svg',

  account: '/icons/menu/account.svg',
  accountOutline: '/icons/menu/account-outline.svg',
  notifications: '/icons/menu/notifications.svg',
  notificationsOutline: '/icons/menu/notifications-outline.svg',
  security: '/icons/menu/security.svg',
  securityOutline: '/icons/menu/security-outline.svg',
  help: '/icons/menu/help.svg',
  helpOutline: '/icons/menu/help-outline.svg',
  organization: '/icons/menu/organisations.svg',
  organizationOutline: '/icons/menu/organisations-outline.svg',
  users: '/icons/menu/users.svg',
  usersOutline: '/icons/menu/users-outline.svg',
  school: '/icons/menu/school.svg',
  schoolOutline: '/icons/menu/school-outline.svg',
  analytics: '/icons/menu/analytics.svg',
  analyticsOutline: '/icons/menu/analytics-outline.svg',
  eventsSettings: '/icons/menu/events.svg',
  eventsSettingsOutline: '/icons/menu/events-outline.svg',
  addOrganization: '/icons/menu/add-organization.svg',
  addOrganizationOutline: '/icons/menu/add-organization-outline.svg',

  tickets: '/icons/myEvents/tickets.svg',
  ticketsOutline: '/icons/myEvents/tickets-outline.svg',
};

export const Header = styled(
  ({
    showActions = true,
    loggedIn = false,
    showLogo = true,
    showSearch = true,
    searchBar,
    className,
  }: HeaderProps): ReactElement => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const router = useRouter();
    const role = useGetRole();
    const { profile } = useProfile();
    const organizationsApi = useOrganizationsApi();

    const { data: allInvites } = useSWR(
      loggedIn ? 'all-org-leader-invites' : null,
      () =>
        organizationsApi.getAllOrgLeaderInvites().then(res => {
          if (!res.success) return [];
          return res.value || [];
        }),
      {
        revalidateOnFocus: false,
      },
    );

    let active: 'home' | 'explore' | 'events' | 'profile' | 'admin' | undefined;

    const path = router?.asPath;
    if (path === '/') active = 'home';
    else if (/^\/explore\/?/.test(path)) active = 'explore';
    else if (/^\/events\/?/.test(path)) active = 'events';
    else if (/^\/(profile|settings)\/?/.test(path)) active = 'profile';
    else if (
      role &&
      [RoleMap['executive-admin'], RoleMap.admin, RoleMap.leader].some(r =>
        role.includes(r),
      ) &&
      /^\/admin\/?/.test(path)
    )
      active = 'admin';

    const leftLoggedInActions = useMemo(() => {
      return (
        <Box
          display="flex"
          gap={16}
          sx={{
            '& .nav-item': {
              display: 'flex',
              transition: 'border-bottom 100ms ease-in-out',
              borderBottom: '2px solid transparent',
              '&.active, &:hover': {
                borderBottom: `2px solid ${colorWhite}`,
              },
            },
            '& .nav': {
              cursor: 'pointer',
              width: 'auto',
              paddingX: 16,
              justifyContent: 'center',
              alignItems: 'center',
              userSelect: 'none',
              textDecoration: 'none',
              color: '#fff',
              fontFamily: 'Poppins',
              fontSize: pxToEm(14),
              fontWeight: 600,
            },
          }}
        >
          <Link
            href={Routes.home()}
            className={clsx('nav-item nav', { active: active === 'home' })}
          >
            <div>
              <p>Home</p>
            </div>
          </Link>
          <Link
            href={Routes.eventTickets()}
            className={clsx('nav-item nav', { active: active === 'events' })}
          >
            <div>
              <p>Check In</p>
            </div>
          </Link>
          <Link
            href={Routes.explore()}
            className={clsx('nav-item nav', { active: active === 'explore' })}
          >
            <div>
              <p>Explore</p>
            </div>
          </Link>
        </Box>
      );
    }, [role, active, allInvites]);

    const loggedInActions = useMemo(() => {
      return (
        <Box
          display="flex"
          sx={{
            gap: 24,
            '& .nav': {
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              width: 100,
              paddingTop: 10,
              paddingBottom: 10,
              borderRadius: 8,
              '&.active .text': {
                fontWeight: 500,
              },
              '&.active,&:hover': {
                background: 'rgba(0,0,0,0.2)',
              },
              justifyContent: 'center',
              alignItems: 'center',
              userSelect: 'none',
              textDecoration: 'none',
              '& .text': {
                color: '#fff',
                fontFamily: 'Poppins',
                fontSize: pxToEm(12),
                fontWeight: 500,
                textDecoration: 'none',
                marginTop: 2,
              },
            },
          }}
        >
          {role &&
            ([RoleMap['executive-admin'], RoleMap.admin].some(r =>
              role.includes(r),
            ) ||
              (role.includes(RoleMap.leader) &&
                allInvites?.some(invite => invite.status === 'accepted'))) && (
              <Box display="flex" alignItems="center" justifyContent="center">
                <ButtonLink
                  href={Routes.createEvent()}
                  variant="vibrant"
                  hoverEffect="none"
                  minHeight={37}
                  sx={{
                    height: 38,
                    width: 152,
                    borderRadius: 29,
                    fontSize: pxToEm(14),
                  }}
                >
                  <Image
                    alt={'Profile'}
                    src="/icons/add-circle.svg"
                    style={{ marginRight: 8 }}
                  />
                  Create Event
                </ButtonLink>
              </Box>
            )}
          {role &&
            ([
              RoleMap['executive-admin'],
              RoleMap.admin,
              RoleMap.approver,
              RoleMap['sequential-approver'],
            ].some(r => role.includes(r)) ||
              (role.includes(RoleMap.leader) &&
                allInvites?.some(invite => invite.status === 'accepted'))) && (
              <Box display="flex" alignItems="center" justifyContent="center">
                <ButtonLink
                  href={
                    role?.includes(RoleMap.approver) ||
                    role?.includes(RoleMap['sequential-approver'])
                      ? Routes.admin.eventRequests()
                      : Routes.admin.default()
                  }
                  variant="vibrant"
                  hoverEffect="none"
                  minHeight={37}
                  sx={{
                    height: 38,
                    width: 111,
                    borderRadius: 29,
                    fontSize: pxToEm(14),
                    backgroundColor: '#FFFFFF',
                    color: '#23305F',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                      color: '#23305F',
                    },
                  }}
                >
                  <Image
                    alt={'Admin'}
                    src="/icons/admin-star.svg"
                    style={{ marginRight: 8 }}
                  />
                  Admin
                </ButtonLink>
              </Box>
            )}

          {loggedIn && (
            <Box
              ml={{ xs: 2, md: 4 }}
              height="inherit"
              alignItems="center"
              display="flex"
            >
              <NotificationButton />
            </Box>
          )}
          <Box display="flex" alignItems="center" justifyContent="center">
            <ButtonLink
              href={Routes.profile()}
              variant="vibrant"
              hoverEffect="none"
              minHeight={37}
              sx={{
                height: 38,
                width: 156,
                borderRadius: 29,
                fontSize: pxToEm(14),
                border: '1px solid #414E7D',
                backgroundColor: '#374473',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#374473',
                  color: '#FFFFFF',
                },
              }}
            >
              {profile?.firstName}
              <Image
                alt={'Profile'}
                src="/icons/menu/profile-outline-bold.svg"
                style={{ marginLeft: 15 }}
              />
            </ButtonLink>
          </Box>
        </Box>
      );
    }, [role, active, allInvites]);

    const loggedOutActions = useMemo(() => {
      return (
        <>
          <Box sx={{ paddingRight: 16 }}>
            <ButtonLink
              href="https://schools.campuslush.com"
              variant="vibrant"
              inverted
              hoverEffect="underline"
              minHeight={37}
              sx={{
                border: 'none',
                color: '#ffffff',
                borderRadius: 0,
              }}
            >
              For Schools
            </ButtonLink>
            <ButtonLink
              href="https://employers.campuslush.com"
              variant="vibrant"
              inverted
              hoverEffect="underline"
              minHeight={37}
              sx={{
                border: 'none',
                color: '#ffffff',
                borderRadius: 0,
              }}
            >
              For Employers
            </ButtonLink>
          </Box>
          <Box
            sx={{
              marginRight: 30,
              paddingRight: 30,
              borderRight: '2px solid rgba(255,255,255,0.5)',
            }}
          >
            <ButtonLink
              href={Routes.download()}
              variant="vibrant"
              inverted
              hoverEffect="underline"
              minHeight={37}
              sx={{
                border: 'none',
                color: '#ffffff',
                borderRadius: 0,
              }}
            >
              Download App
            </ButtonLink>
          </Box>
          <Box mr={30}>
            <ButtonLink
              href={Routes.signUp()}
              variant="vibrant"
              inverted
              hoverEffect="underline"
              minHeight={37}
              sx={{
                border: 'none',
              }}
            >
              Create Account
            </ButtonLink>
          </Box>
          <ButtonLink
            href={Routes.logIn()}
            variant="vibrant"
            hoverEffect="none"
            minHeight={37}
            minWidth={150}
            sx={{
              height: 60,
              borderRadius: 0,
            }}
          >
            Log In
          </ButtonLink>
        </>
      );
    }, []);

    return (
      <>
        {isDesktop && (
          <Box
            className={className}
            sx={{
              height: 'min(15vh, 60px)',
              width: { xs: '100%', md: '100vw' },
              display: 'flex',
              alignItems: 'center',
              borderRadius: 0,
              position: 'sticky',
              top: 0,
              zIndex: 200,
              '& .background': {
                background: theme => theme.palette.primary.main,
                height: 'max(44px, min(15vh, 60px))',
                width: { xs: '100%', md: '100vw' },
                position: 'fixed',
                top: 0,
                zIndex: 195,
              },
            }}
          >
            <Box
              margin="auto"
              width={FULL_WIDTH + 120}
              pl={{ xs: 24, md: scalingPx(32, 60) }}
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              className="background"
            >
              <Box
                pr={32}
                lineHeight={0}
                display="flex"
                alignItems="center"
                gap={32}
              >
                {showLogo && (
                  <Link href={Routes.home()}>
                    <CampusLushLogo />
                  </Link>
                )}
                {showActions && loggedIn && leftLoggedInActions}
              </Box>
              {showSearch && searchBar && (
                <Box
                  display={{ xs: 'none', md: 'block' }}
                  ml="auto"
                  mr={2}
                  height="inherit"
                  alignItems="center"
                >
                  {searchBar}
                </Box>
              )}
              <Box
                display={{ xs: 'none', md: 'block' }}
                ml="auto"
                mr={{ md: 60 }}
                height="inherit"
              >
                {loggedIn ? (
                  <Box
                    flex="0 3 402px"
                    display="flex"
                    justifyContent="space-between"
                    height="inherit"
                  >
                    {showActions && loggedInActions}
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" height="inherit">
                    {showActions && loggedOutActions}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </>
    );
  },
)``;
